import { AppleLogo, GooglePlayLogo } from '@phosphor-icons/react';

/**
 * App Store and Google Play badges. Agreement §6 makes these a contractual
 * surface, so they render in the site footer and on the order confirmation
 * page. Every caller gates them on `site_config.apps_live`, which was checked
 * rather than assumed: both callers do, and the flag is false today.
 *
 * These are typographic stand-ins. Apple and Google both require their own
 * supplied badge artwork, which drops in at app launch: replace the two <a>
 * bodies with the official assets and keep the links and the flag.
 *
 * **The links were the platform vendor's own listings**, `apps.apple.com/app/
 * shopace` and `id=au.com.shopace`, sitting one flag flip away from the
 * client's public footer. Nothing was exposed, because `apps_live` is false,
 * and that is the whole problem with it: turning the flag on is a reasonable
 * thing for somebody to do, and it would have sent this client's customers to
 * a different company's app.
 *
 * So the links are now unset, and unset means the badges do not render at all.
 * A flag cannot publish a link that does not exist. Q-24 asks whether the
 * client has or wants apps; T167 fills these in with their own store links when
 * the answer arrives. R-332.
 */
const APP_STORE_URL: string | null = null;
const PLAY_STORE_URL: string | null = null;
export function StoreBadges({ label = 'Get the app' }: { label?: string }) {
  // Not configured, so there is nothing honest to link to. Render nothing
  // rather than a link to somebody else's product or a dead one.
  if (!APP_STORE_URL || !PLAY_STORE_URL) return null;

  return (
    <div>
      <p className="text-sm font-bold">{label}</p>
      <div className="mt-3 flex flex-wrap gap-3">
        <a
          href={APP_STORE_URL}
          className="inline-flex h-12 items-center gap-2.5 rounded-[10px] bg-ink px-4 text-panel transition-opacity hover:opacity-90"
        >
          <AppleLogo size={22} weight="fill" aria-hidden />
          <span className="text-left leading-tight">
            <span className="block text-[10px] opacity-80">Download on the</span>
            <span className="block text-[14px] font-bold">App Store</span>
          </span>
        </a>
        <a
          href={PLAY_STORE_URL}
          className="inline-flex h-12 items-center gap-2.5 rounded-[10px] bg-ink px-4 text-panel transition-opacity hover:opacity-90"
        >
          <GooglePlayLogo size={22} weight="fill" aria-hidden />
          <span className="text-left leading-tight">
            <span className="block text-[10px] opacity-80">Get it on</span>
            <span className="block text-[14px] font-bold">Google Play</span>
          </span>
        </a>
      </div>
    </div>
  );
}
